import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import BannerSlider from "@/components/BannerSlider";
import CourseCard from "@/components/CourseCard";

const CATEGORIES = ["Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D"];
const INSTRUCTOR_PHOTO = "https://customer-assets.emergentagent.com/job_1791f9d8-ffce-4632-931c-e01b5c5c94c3/artifacts/fn2eo02h_1000003634.jpg";

export default function HomePage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [books, setBooks] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data)).catch((err) => console.error(err));
    api.get("/courses?content_type=course").then((r) => setCourses(r.data)).catch((err) => console.error(err));
    api.get("/courses?content_type=book").then((r) => setBooks(r.data)).catch((err) => console.error(err));
  }, []);

  return (
    <div className="page-with-nav bg-white" data-testid="home-page">
      {/* Banner Slider */}
      <BannerSlider banners={banners} />

      {/* Categories */}
      <div className="px-4 pt-6 pb-2">
        <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Exam Categories
        </h2>
        <div className="flex overflow-x-auto gap-2 scrollbar-hide pb-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`category-${cat.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => navigate(`/courses?category=${encodeURIComponent(cat)}`)}
              className="flex-shrink-0 bg-[#0284C7] text-white text-xs font-bold px-4 py-2.5 transition-none hover:bg-[#0369A1]"
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Latest Courses - Horizontal Scroll */}
      <div className="pt-4 pb-2">
        <div className="flex items-center justify-between px-4 mb-3">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Latest Courses
          </h2>
          <button
            data-testid="view-all-courses-btn"
            onClick={() => navigate("/courses")}
            className="text-xs font-bold text-[#0284C7] flex items-center gap-1 transition-none"
          >
            View All <ChevronRight size={14} />
          </button>
        </div>
        <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x snap-mandatory scrollbar-hide" data-testid="latest-courses-scroll">
          {courses.slice(0, 6).map((course) => (
            <CourseCard key={course.id} course={course} compact />
          ))}
          {courses.length === 0 && (
            <p className="text-sm text-slate-400 py-8">No courses available yet.</p>
          )}
        </div>
      </div>

      {/* Books Section */}
      {books.length > 0 && (
        <div className="pt-2 pb-4 bg-slate-50">
          <div className="flex items-center justify-between px-4 mb-3 pt-4">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Study Materials & Books
            </h2>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x snap-mandatory scrollbar-hide">
            {books.map((book) => (
              <CourseCard key={book.id} course={book} compact />
            ))}
          </div>
        </div>
      )}

      {/* Instructor Section */}
      <div className="bg-slate-900 text-white px-6 py-8">
        <div className="flex items-center gap-4 mb-4">
          <img src={INSTRUCTOR_PHOTO} alt="Instructor" className="w-16 h-16 object-cover" />
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Your Instructor</p>
            <h3 className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Smart English Fast English</h3>
          </div>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">
          Expert in English language teaching for competitive exams. Specialized preparation for West Bengal state exams including Primary TET, Upper Primary TET, C-TET, Group C & Group D.
        </p>
      </div>
    </div>
  );
}
